/**
 * Single sanctioned dialog entry point for the v2 routing utility. Builds
 * the typed `Deps` from RTKQ trigger hooks (the only place that does this),
 * resolves the active strategy, and forwards to `executeOperation`. A
 * `useRef` in-flight guard short-circuits concurrent calls with
 * `CONCURRENT_COMMIT_DETECTED`. Thrown errors become typed `BE_REJECTED`
 * results — the hook never surfaces an unhandled rejection. Dialogs render
 * `lastResult` via `<RouteOpResultToast />`.
 */
import { useCallback, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import api from 'api'
import {
  useAssignDailyStopsMutation,
  useBulkChangeDateMutation,
  useChangeDateDailyStopsMutation,
  useRestoreDailyVersionMutation,
  useUnassignDailyStopsMutation,
  useUpdateDailySequenceMutation,
} from 'api/route/dailyRoute'
import { useAssignDailySlotsMutation, useSetDailySlotSequenceMutation, useUnassignDailySlotsMutation } from 'api/route/dailyRouteSlot'
import { useGetBulkCompleteStopsMutation } from 'api/route/getBulkCompleteStops'
import { useLazyGetRouteStopsPreviewQuery } from 'api/route/getRouteStopsPreview'
import {
  useAssignMasterStopsMutation,
  useChangeDayOfWeekMasterStopsMutation,
  useRestoreMasterVersionMutation,
  useSyncDailyToMasterMutation,
  useUnassignMasterStopsMutation,
  useUpdateMasterSequenceMutation,
} from 'api/route/masterRoute'
import {
  useAssignMasterSlotsMutation,
  useChangeMasterSlotDayOfWeekMutation,
  useSetMasterSlotSequenceMutation,
  useUnassignMasterSlotsMutation,
} from 'api/route/masterRouteSlot'
import { useBulkMoveStopsMutation } from 'api/route/moveStops'
import { adaptDailyResponse, useLazyNormalizedMasterStops } from 'api/route/normalizedStopHooks'
import { useChangeRouteSlotDateMutation, useMoveRouteSlotsMutation } from 'api/route/routeSlot'
import { useRouteManagerV3 } from 'hooks/useRouteManagerV3'
import { clampStrategyToTenant, getEffectiveStrategy } from 'slices/routeOpsStrategyOverride'
import { logRouteOpEvent } from 'utils/routeOpTelemetry'
import { executeOperation } from 'utils/routeStopOperations/v2'
import { extractBackendError, readErrorId } from 'utils/routeStopOperations/v2/backendError'

import type { RouteOpsStrategyRootStateSlice } from 'slices/persistentRouteOpsStrategySlice'
import type { GetDailyStopsResponse } from 'types/api/route/dailyStop'
import type {
  DailySlotUnassignResponse,
  MasterSlotSequenceResponse,
  RouteSlotCountResponse,
  SlotDailySequence,
} from 'types/api/route/routeSlotRequests'
import type { RouteId } from 'types/entities/shared/ids'
import type {
  AssignDailyArgs,
  AssignDailyResponse,
  AssignDailySlotArgs,
  AssignMasterSlotArgs,
  ChangeDateRouteSlotArgs,
  ChangeDayMasterSlotArgs,
  MoveRouteSlotArgs,
  SequenceDailySlotArgs,
  SequenceMasterSlotArgs,
  UnassignDailySlotArgs,
  UnassignMasterSlotArgs,
  AssignMasterArgs,
  AssignMasterResponse,
  BulkChangeDateArgs,
  BulkChangeDateResponse,
  BulkCompleteArgs,
  BulkCompleteResponse,
  BulkMutateArgs,
  BulkMoveStopsResponse,
  ChangeDateDailyArgs,
  ChangeDateDailyResponse,
  ChangeDayMasterArgs,
  ChangeDayMasterResponse,
  DailyStop,
  Deps,
  GetDailyStopsArgs,
  GetRouteStopsPreviewArgs,
  GetRouteStopsPreviewResponse,
  MutationPromise,
  OnRequestStop,
  OperationResult,
  RestoreDailyVersionArgs,
  RestoreDailyVersionResponse,
  RestoreMasterVersionArgs,
  RestoreMasterVersionResponse,
  RouteOpApiSlice,
  RouteOpIntent,
  RouteOpsStrategy,
  SequenceDailyArgs,
  SequenceDailyResponse,
  SequenceMasterArgs,
  SequenceMasterResponse,
  SyncDailyToMasterArgs,
  SyncDailyToMasterResponse,
  TypedLazyQuery,
  TypedTrigger,
  UnassignDailyArgs,
  UnassignDailyResponse,
  UnassignMasterArgs,
  UnassignMasterResponse,
} from 'types/modules/routing'

// ── Public contract ──────────────────────────────────────────────────────

/** Per-call knobs for `run()`. Whatever is passed still goes through `clampStrategyToTenant`. */
export interface RunRouteOperationOptions {
  /** Override the selected strategy for this single call. */
  readonly strategyId?: RouteOpsStrategy
}

export interface UseRouteOperationReturn {
  readonly run: (intent: RouteOpIntent, options?: RunRouteOperationOptions) => Promise<OperationResult>
  readonly isRunning: boolean
  readonly lastResult: OperationResult | null
  readonly reset: () => void
}

// ── Internal helpers ─────────────────────────────────────────────────────

/**
 * RTK Query trigger functions return a `MutationActionCreatorResult` which
 * already carries `.unwrap()` — structurally compatible with our
 * `MutationPromise<T>` contract from `Deps`. The cast is wrapped in a
 * one-line shim so the boundary is grep-able. No `any` is involved. The
 * cast can be dropped once the per-trigger arg/response generics tighten.
 */
const asTrigger = <TArg, TResponse>(
  // The RTK trigger accepts a single argument and returns an object that
  // satisfies the `MutationPromise<unknown>` shape. We narrow at the boundary.
  trigger: (arg: TArg) => MutationPromise<unknown>
): TypedTrigger<TArg, TResponse> => ((arg: TArg) => trigger(arg) as MutationPromise<TResponse>) as TypedTrigger<TArg, TResponse>

const asLazyQuery = <TArg, TResponse>(trigger: (arg: TArg) => MutationPromise<unknown>): TypedLazyQuery<TArg, TResponse> =>
  ((arg: TArg) => trigger(arg) as MutationPromise<TResponse>) as TypedLazyQuery<TArg, TResponse>

/** Pull the routeId off any intent — every intent has one (`BaseIntent`). */
const routeIdFromIntent = (intent: RouteOpIntent): RouteId => intent.routeId

/**
 * A thrown error, typed as `BE_REJECTED` so the hook never propagates an
 * exception to the dialog. Provenance goes on `stepId`, keeping `message` showable.
 */
const buildThrownErrorResult = (cause: unknown): OperationResult => {
  const { status, message } = extractBackendError(cause)
  return {
    success: false,
    executedPhases: [],
    committedSequences: [],
    cachesInvalidated: false,
    error: {
      phase: 'PREFLIGHT',
      stepId: 'useRouteOperation:thrown',
      cause: {
        kind: 'BE_REJECTED',
        phase: 'PREFLIGHT',
        status,
        message,
      },
    },
  }
}

/** Build a synthetic `CONCURRENT_COMMIT_DETECTED` result without invoking the executor. */
const buildConcurrentResult = (routeId: RouteId): OperationResult => ({
  success: false,
  executedPhases: [],
  committedSequences: [],
  cachesInvalidated: false,
  error: {
    phase: 'PREFLIGHT',
    stepId: 'useRouteOperation:guard',
    cause: { kind: 'CONCURRENT_COMMIT_DETECTED', routeId },
  },
})

// ── Hook ─────────────────────────────────────────────────────────────────

// The only place that knows an operation failed and why: the executor's logger no-ops unless
// `window.__debugRouteOps` is set, and `handleError` only toasts. Without this a dispatcher's failed
// move leaves nothing queryable, so incidence can only be counted by watching sessions one at a time.
const reportFailure = (intent: RouteOpIntent, strategy: RouteOpsStrategy, result: OperationResult): void => {
  const cause = result.error?.cause
  const beRejected = cause?.kind === 'BE_REJECTED' ? cause : undefined
  logRouteOpEvent('operation.failed', {
    intent: intent.kind,
    strategy,
    phase: result.error?.phase,
    stepId: result.error?.stepId,
    errorKind: cause?.kind,
    status: beRejected?.status,
    // The id, never the message: the rest of the backend's text can name an account.
    errorId: beRejected ? readErrorId(beRejected.message) : undefined,
    phaseCount: result.executedPhases.length,
    cachesInvalidated: result.cachesInvalidated,
  })
}

export const useRouteOperation = (): UseRouteOperationReturn => {
  const dispatch = useDispatch()
  const isRouteManagerV3Enabled = useRouteManagerV3()
  const strategy = useSelector((state: RouteOpsStrategyRootStateSlice) => getEffectiveStrategy(state, isRouteManagerV3Enabled))

  // RTKQ mutation triggers — same set as the v1 hook, replicated independently
  // so the v2 utility never reaches into v1 builders. Tuples are unwrapped
  // here so `Deps` carries plain trigger functions.
  const [unassignDailyTrigger] = useUnassignDailyStopsMutation()
  const [assignDailyTrigger] = useAssignDailyStopsMutation()
  const [changeDateDailyTrigger] = useChangeDateDailyStopsMutation()
  const [sequenceDailyTrigger] = useUpdateDailySequenceMutation()
  const [unassignMasterTrigger] = useUnassignMasterStopsMutation()
  const [assignMasterTrigger] = useAssignMasterStopsMutation()
  const [sequenceMasterTrigger] = useUpdateMasterSequenceMutation()
  const [changeDayMasterTrigger] = useChangeDayOfWeekMasterStopsMutation()
  const [bulkMutateTrigger] = useBulkMoveStopsMutation()
  const [bulkChangeDateTrigger] = useBulkChangeDateMutation()
  const [bulkCompleteTrigger] = useGetBulkCompleteStopsMutation()
  const [restoreMasterVersionTrigger] = useRestoreMasterVersionMutation()
  const [restoreDailyVersionTrigger] = useRestoreDailyVersionMutation()
  const [syncDailyToMasterTrigger] = useSyncDailyToMasterMutation()
  // v3 slot triggers — optional on `Deps`, so only the `v3-slot` strategy reads them.
  const [assignDailySlotTrigger] = useAssignDailySlotsMutation()
  const [unassignDailySlotTrigger] = useUnassignDailySlotsMutation()
  const [sequenceDailySlotTrigger] = useSetDailySlotSequenceMutation()
  const [assignMasterSlotTrigger] = useAssignMasterSlotsMutation()
  const [unassignMasterSlotTrigger] = useUnassignMasterSlotsMutation()
  const [sequenceMasterSlotTrigger] = useSetMasterSlotSequenceMutation()
  const [changeDayMasterSlotTrigger] = useChangeMasterSlotDayOfWeekMutation()
  const [moveRouteSlotTrigger] = useMoveRouteSlotsMutation()
  const [changeDateRouteSlotTrigger] = useChangeRouteSlotDateMutation()
  const [getMasterStops] = useLazyNormalizedMasterStops()
  const [getRouteStopsPreviewTrigger] = useLazyGetRouteStopsPreviewQuery()

  const [lastResult, setLastResult] = useState<OperationResult | null>(null)
  const [isRunning, setIsRunning] = useState<boolean>(false)
  const inFlightRef = useRef<boolean>(false)

  // `getDailyStops` is dispatched via `api.endpoints.*.initiate` (matching the
  // v1 hook) — RTKQ's lazy hook would re-subscribe; we want a one-shot read
  // that bypasses cache when the executor's verifier asks for fresh state.
  // `api` is a JS module today; the `endpoints.getDailyStops` path is untyped
  // at the import. Narrow at the boundary via `unknown`.
  const apiEndpoints = (
    api as unknown as {
      readonly endpoints: {
        readonly getDailyStops: {
          readonly initiate: (arg: GetDailyStopsArgs, options: { readonly forceRefetch: boolean; readonly subscribe: boolean }) => unknown
        }
      }
    }
  ).endpoints

  const getDailyStops = useCallback(
    (args: GetDailyStopsArgs): MutationPromise<ReadonlyArray<DailyStop | OnRequestStop>> => {
      const raw = dispatch(
        apiEndpoints.getDailyStops.initiate(args, {
          forceRefetch: true,
          subscribe: false,
        }) as never
      ) as unknown as MutationPromise<GetDailyStopsResponse>
      const wrapped = raw.then(response => adaptDailyResponse(args, response)) as MutationPromise<ReadonlyArray<DailyStop | OnRequestStop>>
      wrapped.unwrap = async () => adaptDailyResponse(args, await raw.unwrap())
      return wrapped
    },
    [dispatch, apiEndpoints]
  )

  // The `api` slice is a JS module; narrow to the typed `RouteOpApiSlice`
  // surface the executor consumes. Only `util.invalidateTags`
  // is used; widening the contract requires adding fields to `RouteOpApiSlice`,
  // not casting.
  const apiSlice = api as unknown as RouteOpApiSlice

  const deps = useMemo<Deps>(
    () => ({
      assignDaily: asTrigger<AssignDailyArgs, AssignDailyResponse>(assignDailyTrigger),
      unassignDaily: asTrigger<UnassignDailyArgs, UnassignDailyResponse>(unassignDailyTrigger),
      sequenceDaily: asTrigger<SequenceDailyArgs, SequenceDailyResponse>(sequenceDailyTrigger),
      changeDateDaily: asTrigger<ChangeDateDailyArgs, ChangeDateDailyResponse>(changeDateDailyTrigger),

      assignMaster: asTrigger<AssignMasterArgs, AssignMasterResponse>(assignMasterTrigger),
      unassignMaster: asTrigger<UnassignMasterArgs, UnassignMasterResponse>(unassignMasterTrigger),
      sequenceMaster: asTrigger<SequenceMasterArgs, SequenceMasterResponse>(sequenceMasterTrigger),
      changeDayMaster: asTrigger<ChangeDayMasterArgs, ChangeDayMasterResponse>(changeDayMasterTrigger),
      restoreMasterVersion: asTrigger<RestoreMasterVersionArgs, RestoreMasterVersionResponse>(restoreMasterVersionTrigger),
      restoreDailyVersion: asTrigger<RestoreDailyVersionArgs, RestoreDailyVersionResponse>(restoreDailyVersionTrigger),
      syncDailyToMaster: asTrigger<SyncDailyToMasterArgs, SyncDailyToMasterResponse>(syncDailyToMasterTrigger),

      getDailyStops,
      getMasterStops,
      getRouteStopsPreview: asLazyQuery<GetRouteStopsPreviewArgs, GetRouteStopsPreviewResponse>(getRouteStopsPreviewTrigger),

      assignDailySlot: asTrigger<AssignDailySlotArgs, RouteSlotCountResponse>(assignDailySlotTrigger),
      unassignDailySlot: asTrigger<UnassignDailySlotArgs, DailySlotUnassignResponse>(unassignDailySlotTrigger),
      sequenceDailySlot: asTrigger<SequenceDailySlotArgs, SlotDailySequence>(sequenceDailySlotTrigger),

      assignMasterSlot: asTrigger<AssignMasterSlotArgs, RouteSlotCountResponse>(assignMasterSlotTrigger),
      unassignMasterSlot: asTrigger<UnassignMasterSlotArgs, RouteSlotCountResponse>(unassignMasterSlotTrigger),
      sequenceMasterSlot: asTrigger<SequenceMasterSlotArgs, MasterSlotSequenceResponse>(sequenceMasterSlotTrigger),
      changeDayMasterSlot: asTrigger<ChangeDayMasterSlotArgs, RouteSlotCountResponse>(changeDayMasterSlotTrigger),

      moveRouteSlot: asTrigger<MoveRouteSlotArgs, RouteSlotCountResponse>(moveRouteSlotTrigger),
      changeDateRouteSlot: asTrigger<ChangeDateRouteSlotArgs, RouteSlotCountResponse>(changeDateRouteSlotTrigger),

      bulkMutate: asTrigger<BulkMutateArgs, BulkMoveStopsResponse>(bulkMutateTrigger),
      bulkChangeDate: asTrigger<BulkChangeDateArgs, BulkChangeDateResponse>(bulkChangeDateTrigger),
      bulkComplete: asTrigger<BulkCompleteArgs, BulkCompleteResponse>(bulkCompleteTrigger),

      dispatch,
      api: apiSlice,
    }),
    [
      assignDailyTrigger,
      unassignDailyTrigger,
      sequenceDailyTrigger,
      changeDateDailyTrigger,
      assignMasterTrigger,
      unassignMasterTrigger,
      sequenceMasterTrigger,
      changeDayMasterTrigger,
      assignDailySlotTrigger,
      unassignDailySlotTrigger,
      sequenceDailySlotTrigger,
      assignMasterSlotTrigger,
      unassignMasterSlotTrigger,
      sequenceMasterSlotTrigger,
      changeDayMasterSlotTrigger,
      moveRouteSlotTrigger,
      changeDateRouteSlotTrigger,
      bulkMutateTrigger,
      bulkChangeDateTrigger,
      bulkCompleteTrigger,
      restoreMasterVersionTrigger,
      restoreDailyVersionTrigger,
      syncDailyToMasterTrigger,
      getDailyStops,
      getMasterStops,
      getRouteStopsPreviewTrigger,
      dispatch,
      apiSlice,
    ]
  )

  const run = useCallback(
    async (intent: RouteOpIntent, options?: RunRouteOperationOptions): Promise<OperationResult> => {
      // In-flight guard: synchronous, ref-based, set BEFORE the await so a
      // second synchronous call cannot race past the check.
      if (inFlightRef.current) {
        const guarded = buildConcurrentResult(routeIdFromIntent(intent))
        setLastResult(guarded)
        return guarded
      }
      inFlightRef.current = true
      setIsRunning(true)

      // Per-call override wins over the persisted slice; the tenant gate outranks both.
      const effectiveStrategy = clampStrategyToTenant(options?.strategyId ?? strategy, isRouteManagerV3Enabled)

      let result: OperationResult
      try {
        result = await executeOperation(intent, deps, { strategyId: effectiveStrategy })
      } catch (cause: unknown) {
        result = buildThrownErrorResult(cause)
      } finally {
        inFlightRef.current = false
        setIsRunning(false)
      }
      if (!result.success) reportFailure(intent, effectiveStrategy, result)
      setLastResult(result)
      return result
    },
    [deps, strategy, isRouteManagerV3Enabled]
  )

  const reset = useCallback(() => {
    setLastResult(null)
  }, [])

  return useMemo<UseRouteOperationReturn>(
    () => ({
      run,
      isRunning,
      lastResult,
      reset,
    }),
    [run, isRunning, lastResult, reset]
  )
}

// Re-export the route-id helper for tests / callers that need to construct a
// branded id at the boundary. Not exported by default — kept internal-ish.
export { asRouteId as __asRouteIdForTests } from 'types/entities/shared/ids'
